import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import { AGURenderService, AGUAppLocation, AGUAppModuleAccent, AGUSortOption, AGUTabStripTab, AGUToastService, IsModuleReentry, ParseIsoDate } from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { RaCompositionState } from '../../ra-composition.state';
import { ResearchApiService } from '../../services/research-api.service';
import { MemberItemStateService } from '../../services/member-item-state.service';
import { ItemKind, ResearchGrantMatch, ResearchItemState, ResearchJobMatch, ResearchPersonMatch, ResearchSavedItems } from '../../models/research.models';
import { PickAccents } from '../dashboard/ra-dashboard.component';
import { DaysUntilClose } from '../shared/grant-dates';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures } from '../shared/load-failures';
import { ListErrorCopy, SAVED_EMPTY_COPY, STATE_NOUN, StatePanelCopy } from '../shared/state-copy';
import { PortalConfigService } from '../../services/portal-config.service';
import { NextResultsPanelId } from '../shared/panel-id';
import { PinnedFirstBy } from '../shared/pinned-first';

/** Tabs this board offers — 'all' plus the three item kinds (mockup: Saved.txt / agu-member-portal-sep-1.html "Saved" screen). */
export type SavedTabKey = 'all' | ItemKind;

/** Sort choices for the Saved board — mirrors the mockup's `SORTS.saved` list. */
export type SavedSortKey = 'recent' | 'due' | 'az';

/** How long the inline "Removed from saved… Undo" alert stays up before it self-clears — same window as MemberItemStateService's dismiss Undo. */
const UNDO_WINDOW_MS = 6000;
/** The page header's meta badge counts saved grants closing within this many days (mockup: `n>=0 && n<=30`). */
const DUE_SOON_WINDOW_DAYS = 30;

const MONTH_ABBR: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TAB_LABELS: ReadonlyArray<{ key: SavedTabKey; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'grant', label: 'Grants' },
    { key: 'person', label: 'People' },
    { key: 'job', label: 'Jobs' },
];

const SORT_OPTIONS: ReadonlyArray<AGUSortOption> = [
    { Key: 'recent', Label: 'Recently saved' },
    { Key: 'due', Label: 'Deadline soonest' },
    { Key: 'az', Label: 'Title A-Z' },
];

const SAVED_SORT_KEYS: readonly SavedSortKey[] = ['recent', 'due', 'az'];

function IsSavedSortKey(value: string): value is SavedSortKey {
    return (SAVED_SORT_KEYS as readonly string[]).includes(value);
}

const SAVED_TAB_KEYS: readonly SavedTabKey[] = ['all', 'grant', 'person', 'job'];

function IsSavedTabKey(value: string): value is SavedTabKey {
    return (SAVED_TAB_KEYS as readonly string[]).includes(value);
}

/**
 * "Due 12 Sep · 23d", or null once the deadline has passed.
 *
 * One shape, exactly the mockup's (`'Due '+fmt(it.due)+' · '+n+'d'`,
 * saved.html:385), rendered ONLY while `n >= 0` — the same guard the mockup
 * applies (`(n!=null && n>=0) ? '<span class="duetag">…' : ''`). The live
 * `· today` and `· closed` variants were invented copy: the mockup has no
 * treatment for either, and R-F18 says render nothing rather than invent one.
 * A grant whose deadline has passed still shows its date in the card's own
 * `Closes` fact, which is where a past deadline belongs.
 *
 * `days` is the caller's already-computed day count (never re-derived here —
 * see `BuildGrantRow`, the one place `new Date()` is read for this row).
 */
export function FormatDueChip(closing: Date, days: number): string | null {
    if (days < 0) return null;
    return `Due ${closing.getUTCDate()} ${MONTH_ABBR[closing.getUTCMonth()]} · ${days}d`;
}

/**
 * One saved grant, with every board-only display field computed once at
 * load time (`ApplyLoaded`/`BuildGrantRow`) rather than re-derived by a
 * template getter — a getter reading `new Date()` on every change-detection
 * pass is exactly the "never bind time-dependent calls in templates" trap
 * (ui-best-practices §7), which `GrantCardComponent.ClosingLine` and
 * `JobCardComponent.MetaLine` (Tasks 16/18) already avoid the same way.
 */
export interface SavedGrantRow {
    kind: 'grant';
    key: string;
    title: string;
    grant: ResearchGrantMatch;
    savedAt: Date | null;
    /** Days until `grant.closingDate`, or null when there's no stated (non-rolling) deadline. */
    dueDays: number | null;
    dueChip: string | null;
}

export interface SavedPersonRow {
    kind: 'person';
    key: string;
    title: string;
    person: ResearchPersonMatch;
    savedAt: Date | null;
}

export interface SavedJobRow {
    kind: 'job';
    key: string;
    title: string;
    job: ResearchJobMatch;
    savedAt: Date | null;
}

export type SavedRow = SavedGrantRow | SavedPersonRow | SavedJobRow;

export function IsGrantRow(row: SavedRow): row is SavedGrantRow {
    return row.kind === 'grant';
}

export function IsPersonRow(row: SavedRow): row is SavedPersonRow {
    return row.kind === 'person';
}

/** The row's pinned flag, wherever its kind keeps it. */
export function RowIsPinned(row: SavedRow): boolean {
    if (IsGrantRow(row)) return row.grant.itemState.pinned;
    if (IsPersonRow(row)) return row.person.itemState.pinned;
    return row.job.itemState.pinned;
}

export function IsJobRow(row: SavedRow): row is SavedJobRow {
    return row.kind === 'job';
}

/** The item most recently removed from the board, still inside its Undo window — `row` is the exact object to reinsert on Undo. */
interface LastRemoved {
    kind: ItemKind;
    key: string;
    title: string;
    row: SavedRow;
}

/**
 * The Saved board — every grant, collaborator and job the member has
 * bookmarked, in one place (Task 19). Reuses the three finder cards
 * (`ra-grant-card`/`ra-person-card`/`ra-job-card`, Tasks 16-18) as-is with
 * `ShowPin=false`/`ShowDismiss=false`; the card's own bookmark toggle
 * (`agu-item-card`'s header button, already labelled "Remove from saved"
 * when `Saved` is true) IS this board's Remove affordance — `OnCardSave`
 * below just routes its `false` emission to `Toggle(kind, key, { saved:
 * false })` plus a local Undo, instead of adding a second remove control.
 *
 * Root component of the `saved` module — rendered via `ngComponentOutlet`
 * with no inputs, same as every other RA module (`GrantFinderPageComponent`
 * et al.).
 *
 * Product states (PD §5): 3 skeleton cards while loading, the error panel ("We could not load saved
 * items", :115) and "Nothing saved yet" (:104). Truthfulness edit: the empty panel drops ", with deadline reminders" (:104) —
 * the hub sends no reminders.
 */
@Component({
    standalone: false,
    selector: 'ra-saved',
    templateUrl: './saved-page.component.html',
    styleUrl: './saved-page.component.scss',
    // The mockup's `.main` is a full-bleed track with 32px of gutter, not a
    // centred 1200px column (saved.html shared block :49) — see `.ra-fullwidth`
    // in styles.scss.
    host: { class: 'ra-fullwidth' },
})
export class SavedPageComponent implements OnInit, OnDestroy {
    /** The results region this page's tab strip drives, unique per instance so two RA tabs never collide on `aria-controls` (review M-9). */
    readonly ResultsPanelId = NextResultsPanelId('saved');

    readonly SortOptions = SORT_OPTIONS;
    /** Each kind's colour, resolved once from the module that owns it — Grants teal, Collaborators blue, Jobs orange (the mockup's per-card `--c`). */
    private readonly accents: Readonly<Record<ItemKind, AGUAppModuleAccent>>;
    /** Bound so the template can narrow `SavedRow` via `@if` — see `IsGrantRow` et al. above. */
    readonly IsGrantRow = IsGrantRow;
    readonly IsPersonRow = IsPersonRow;
    readonly IsJobRow = IsJobRow;

    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    readonly EmptyCopy: StatePanelCopy = SAVED_EMPTY_COPY;
    readonly ErrorCopy: StatePanelCopy = ListErrorCopy(STATE_NOUN.Saved);

    grantRows: SavedGrantRow[] = [];
    personRows: SavedPersonRow[] = [];
    jobRows: SavedJobRow[] = [];

    activeTab: SavedTabKey = 'all';
    searchQuery = '';
    sort: SavedSortKey = 'recent';

    /**
     * Set once per `Load()`, not template getters — both depend on "now"
     * (via `ParseIsoDate` + a single captured `Date` in `ApplyLoaded`), so a
     * getter would re-run the deadline math on every change-detection pass.
     */
    Subtitle = "0 items you're keeping an eye on";
    DueSoonCount = 0;
    /** The mockup's `.modenote` line under the header (saved.html:500-503) — the deadline warning when there is one, else what the board is. */
    ModeNote = 'Everything you have bookmarked across grants, people and jobs.';

    lastRemoved: LastRemoved | null = null;
    private removedTimer: ReturnType<typeof setTimeout> | null = null;

    private locationSub: Subscription | null = null;
    /**
     * Module the surface was on at the last `Location$` emission. Seeded with
     * this board's own key so the emission that arrives on subscribe — which is
     * always this module, since the board only mounts once it is opened — is not
     * mistaken for a re-entry and does not load a second time on top of
     * `ngOnInit`.
     */
    private previousModuleKey: string = RA_MODULE_KEYS.Saved;

    constructor(
        private readonly api: ResearchApiService,
        private readonly itemState: MemberItemStateService,
        private readonly host: ExplorerRaHostAdapter,
        private readonly render: AGURenderService,
        composition: RaCompositionState,
        /** The phone shell's toast, surface-provided (P/Member_Hub.html:953-958); absent outside a surface. */
        @Optional() @Inject(AGUToastService) private readonly toast: AGUToastService | null = null,
        /** The memoised portal config, for the error panel's Contact support address (PD §5); absent outside a surface. */
        @Optional() @Inject(PortalConfigService) private readonly portalConfig: PortalConfigService | null = null
    ) {
        // Read from each kind's own module definition rather than restated
        // here, exactly as Home's pick rails do — re-accenting Jobs in
        // RA_MODULE_DEFINITIONS re-inks every job card on this board too.
        this.accents = PickAccents((moduleKey) => composition.AccentOf(moduleKey));
    }

    ngOnInit(): void {
        void this.failures.ReadSupport(this.portalConfig);
        this.locationSub = this.host.Location$.subscribe((loc) => this.OnLocationChanged(loc));
        void this.Load();
    }

    ngOnDestroy(): void {
        this.locationSub?.unsubscribe();
        if (this.removedTimer !== null) clearTimeout(this.removedTimer);
    }

    /**
     * The surface keeps every visited module MOUNTED (see `WithMountedModule`),
     * so this board is not re-created when the member comes back to it — and its
     * contents are exactly what the finders change while they are on screen.
     * Re-entry therefore re-loads, which is the same freshness a
     * destroy-and-recreate surface got from `ngOnInit`. Moving around inside the
     * board is not re-entry and does not reload.
     */
    private OnLocationChanged(loc: AGUAppLocation): void {
        const reentered = IsModuleReentry(RA_MODULE_KEYS.Saved, this.previousModuleKey, loc.ModuleKey);
        this.previousModuleKey = loc.ModuleKey;
        if (reentered) void this.Load();
    }

    // ── Load ─────────────────────────────────────────────────────────────

    async Load(): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const result = await this.api.SavedItems(null);
            this.ApplyLoaded(result);
            this.failures.Reset();
        } catch (e) {
            this.error = DescribeError(e);
            this.failures.Record(e);
        } finally {
            this.loading = false;
        }
    }

    Retry(): void {
        void this.Load();
    }

    /** The empty panel's "Browse grants" / "Browse jobs" (P/proto-states.js:104): that finder's search view. */
    OnBrowse(moduleKey: typeof RA_MODULE_KEYS.Grants | typeof RA_MODULE_KEYS.Jobs): void {
        this.host.NavigateTo({ ModuleKey: moduleKey, View: 'search' });
    }

    private ApplyLoaded(result: ResearchSavedItems): void {
        const now = new Date();
        const savedAtByKey = new Map<string, Date>();
        for (const stamp of result.savedAt) {
            const parsed = ParseIsoDate(stamp.savedAt);
            if (parsed) savedAtByKey.set(`${stamp.kind}:${stamp.key}`, parsed);
        }

        this.grantRows = result.grants.map((grant) => this.BuildGrantRow(grant, now, savedAtByKey));
        this.personRows = result.people.map((person) => this.BuildPersonRow(person, savedAtByKey));
        this.jobRows = result.jobs.map((job) => this.BuildJobRow(job, savedAtByKey));

        this.itemState.Prime(
            'grant',
            result.grants.map((g) => ({ key: g.contentID, itemState: g.itemState }))
        );
        this.itemState.Prime(
            'person',
            result.people.map((p) => ({ key: p.contributorID, itemState: p.itemState }))
        );
        this.itemState.Prime(
            'job',
            result.jobs.map((j) => ({ key: j.jobId, itemState: j.itemState }))
        );

        this.RefreshHeaderCounts();
    }

    /**
     * Recomputes the header's subtitle and deadline badge from the rows that are
     * actually on the board. Called after every load AND after every local row
     * change: removing the only saved item used to leave the subtitle reading
     * "1 item you're keeping an eye on" until the next reload (live finding
     * #23). Fields rather than getters because `dueDays` is time-derived —
     * a getter would re-run the deadline math on every change-detection pass
     * (ui-best-practices §7).
     */
    private RefreshHeaderCounts(): void {
        const savedCount = this.grantRows.length + this.personRows.length + this.jobRows.length;
        this.Subtitle = `${savedCount} item${savedCount === 1 ? '' : 's'} you're keeping an eye on`;
        // Same rule as the server's `CountSavedClosingSoon` (B-5): open, non-rolling (`dueDays` is
        // null for rolling/undated), closing today through day 30. `isExpired` is checked as well as
        // `dueDays >= 0` because a grant the feed moved to 'Grants Expired' can still carry a future
        // date — the client has no `Source`, but `isExpired` is the server's verdict that includes it.
        this.DueSoonCount = this.grantRows.filter(
            (r) => !r.grant.isExpired && r.dueDays !== null && r.dueDays >= 0 && r.dueDays <= DUE_SOON_WINDOW_DAYS
        ).length;
        // The mockup's own two sentences, verbatim (`paintCounts()`, saved.html:500-503).
        this.ModeNote =
            this.DueSoonCount > 0
                ? `${this.DueSoonCount} saved item${this.DueSoonCount === 1 ? ' has' : 's have'} a deadline in the next 30 days.`
                : 'Everything you have bookmarked across grants, people and jobs.';
    }

    private BuildGrantRow(grant: ResearchGrantMatch, now: Date, savedAtByKey: ReadonlyMap<string, Date>): SavedGrantRow {
        // The deadline count is `DaysUntilClose`'s, not a local instant diff:
        // both sides have to be whole UTC calendar days or a grant closing
        // TODAY rounds to −1 from midday UTC and silently loses its chip, drops
        // out of the header's 30-day sentence and mis-sorts under "Deadline
        // soonest" — all while the same grant's card still reads `Closes …
        // today` from the very same helper (R-F20).
        let dueDays: number | null = null;
        let dueChip: string | null = null;
        const closing = grant.closingIsRolling ? null : ParseIsoDate(grant.closingDate);
        if (closing) {
            dueDays = DaysUntilClose(grant, now);
            if (dueDays !== null) dueChip = FormatDueChip(closing, dueDays);
        }
        return {
            kind: 'grant',
            key: grant.contentID,
            title: grant.title,
            grant,
            savedAt: savedAtByKey.get(`grant:${grant.contentID}`) ?? null,
            dueDays,
            dueChip,
        };
    }

    private BuildPersonRow(person: ResearchPersonMatch, savedAtByKey: ReadonlyMap<string, Date>): SavedPersonRow {
        return {
            kind: 'person',
            key: person.contributorID,
            title: person.name,
            person,
            savedAt: savedAtByKey.get(`person:${person.contributorID}`) ?? null,
        };
    }

    private BuildJobRow(job: ResearchJobMatch, savedAtByKey: ReadonlyMap<string, Date>): SavedJobRow {
        return {
            kind: 'job',
            key: job.jobId,
            title: job.title,
            job,
            savedAt: savedAtByKey.get(`job:${job.jobId}`) ?? null,
        };
    }

    // ── Tabs / search / sort ────────────────────────────────────────────

    /**
     * Label only, no count: the mockup's board tabs carry none (saved.html:344-349),
     * and R-F18 rules that the mockup wins. `AGUTabStripTab.Count` is left
     * absent rather than set to null so the strip renders no badge element at
     * all — the board's own totals are already in the header's subtitle
     * ("N items you're keeping an eye on").
     */
    get Tabs(): AGUTabStripTab[] {
        return TAB_LABELS.map((t) => ({ Key: t.key, Label: t.label }));
    }

    /** The colour a row of `kind` paints with — drives the grid cell's `[data-accent]`, which the stylesheet turns into that cell's `--accent` cascade. */
    AccentFor(kind: ItemKind): AGUAppModuleAccent {
        return this.accents[kind];
    }

    OnTabChange(key: string): void {
        if (IsSavedTabKey(key)) {
            this.activeTab = key;
        }
    }

    OnSearchChange(value: string): void {
        this.searchQuery = value;
    }

    /** The mockup's `.srch` is a native input, so the board reads its value off the event rather than through an MJ search component's `ValueChange`. */
    OnSearchInput(event: Event): void {
        const input = event.target;
        if (input instanceof HTMLInputElement) this.OnSearchChange(input.value);
    }

    /** The sort `<select>` emits a plain option key — a key this board does not own leaves the sort as it was, rather than sorting by nothing. */
    OnSortChange(key: string): void {
        if (IsSavedSortKey(key)) this.sort = key;
    }

    OnClear(): void {
        this.activeTab = 'all';
        this.searchQuery = '';
    }

    get AllRows(): SavedRow[] {
        return [...this.grantRows, ...this.personRows, ...this.jobRows];
    }

    get FilteredRows(): SavedRow[] {
        const scoped = this.activeTab === 'all' ? this.AllRows : this.AllRows.filter((r) => r.kind === this.activeTab);
        const q = this.searchQuery.trim().toLowerCase();
        const matched = q ? scoped.filter((r) => this.SearchText(r).includes(q)) : scoped;
        // Pinned rows lead under every sort; the chosen sort orders each partition.
        return PinnedFirstBy([...matched].sort((a, b) => this.CompareRows(a, b)), RowIsPinned);
    }

    private SearchText(row: SavedRow): string {
        if (IsGrantRow(row)) return `${row.title} ${row.grant.grantor ?? ''}`.toLowerCase();
        if (IsPersonRow(row)) return `${row.title} ${row.person.organization ?? ''} ${row.person.jobTitle ?? ''}`.toLowerCase();
        return `${row.title} ${row.job.companyName ?? ''}`.toLowerCase();
    }

    private CompareRows(a: SavedRow, b: SavedRow): number {
        if (this.sort === 'az') return a.title.localeCompare(b.title);
        if (this.sort === 'due') return this.CompareDue(a, b);
        return this.CompareRecent(a, b);
    }

    /** Grants with a stated closing date sort ascending by that date; everything else (including grants with no date) falls back to Recently saved. */
    private CompareDue(a: SavedRow, b: SavedRow): number {
        const aDue = IsGrantRow(a) ? a.dueDays : null;
        const bDue = IsGrantRow(b) ? b.dueDays : null;
        if (aDue !== null && bDue !== null) return aDue - bDue;
        if (aDue !== null) return -1;
        if (bDue !== null) return 1;
        return this.CompareRecent(a, b);
    }

    private CompareRecent(a: SavedRow, b: SavedRow): number {
        const aTime = a.savedAt?.getTime() ?? 0;
        const bTime = b.savedAt?.getTime() ?? 0;
        return bTime - aTime;
    }

    // ── Empty states ────────────────────────────────────────────────────

    get IsEmpty(): boolean {
        return this.FilteredRows.length === 0;
    }

    get HasAnySaved(): boolean {
        return this.AllRows.length > 0;
    }

    // ── Card actions ────────────────────────────────────────────────────

    /** `agu-item-card`'s bookmark toggle: `Saved` is always true for a board row, so a click always emits `false` — this IS the Remove affordance (see class doc). A stray `true` (shouldn't happen from this page) is a no-op. */
    async OnCardSave(row: SavedRow, saved: boolean): Promise<void> {
        if (saved) return;
        await this.Remove(row);
    }

    private async Remove(row: SavedRow): Promise<void> {
        await this.itemState.Toggle(row.kind, row.key, { saved: false });
        this.toast?.ShowSaved(false); // The bookmark toast (P/Member_Hub.html:953-966): a board bookmark tap is always a removal.
        this.RemoveRowLocally(row);
        this.ShowUndo(row);
    }

    async Undo(): Promise<void> {
        const removed = this.lastRemoved;
        if (!removed) return;
        if (this.removedTimer !== null) {
            clearTimeout(this.removedTimer);
            this.removedTimer = null;
        }
        this.lastRemoved = null;
        const state = await this.itemState.Toggle(removed.kind, removed.key, { saved: true });
        this.ReinsertRow(this.WithItemState(removed.row, state));
    }

    private RemoveRowLocally(row: SavedRow): void {
        if (IsGrantRow(row)) this.grantRows = this.grantRows.filter((r) => r.key !== row.key);
        else if (IsPersonRow(row)) this.personRows = this.personRows.filter((r) => r.key !== row.key);
        else this.jobRows = this.jobRows.filter((r) => r.key !== row.key);
        this.RefreshHeaderCounts();
    }

    private ReinsertRow(row: SavedRow): void {
        if (IsGrantRow(row)) this.grantRows = [...this.grantRows, row];
        else if (IsPersonRow(row)) this.personRows = [...this.personRows, row];
        else this.jobRows = [...this.jobRows, row];
        this.RefreshHeaderCounts();
    }

    /** Patches the reinserted row's nested match object with the server-confirmed item state from `Undo`'s `Toggle` call, so the card's bookmark icon reflects `saved: true` rather than the stale pre-removal snapshot. */
    private WithItemState(row: SavedRow, state: ResearchItemState): SavedRow {
        if (IsGrantRow(row)) return { ...row, grant: { ...row.grant, itemState: state } };
        if (IsPersonRow(row)) return { ...row, person: { ...row.person, itemState: state } };
        return { ...row, job: { ...row.job, itemState: state } };
    }

    private ShowUndo(row: SavedRow): void {
        if (this.removedTimer !== null) clearTimeout(this.removedTimer);
        this.lastRemoved = { kind: row.kind, key: row.key, title: row.title, row };
        this.removedTimer = setTimeout(() => {
            this.removedTimer = null;
            this.lastRemoved = null;
            // A timer firing is not a user event and not an API call, so it is
            // the one other place this zoneless host needs telling to repaint.
            this.render.Refresh();
        }, UNDO_WINDOW_MS);
    }

    // ── Navigation ──────────────────────────────────────────────────────

    /** Opens a row in the module that owns its kind — one entry point, so the template binds `(Open)` identically on all three cards. */
    OnOpenRow(row: SavedRow): void {
        // Saved is the crumb's origin: the detail reads "Back to Saved" and returns here (PD §6).
        const from = this.host.CurrentLocation;
        if (IsGrantRow(row)) this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Grants, View: 'detail', RecordID: row.key, From: from });
        else if (IsPersonRow(row)) this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.People, View: 'profile', RecordID: row.key, From: from });
        else this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.Jobs, View: 'detail', RecordID: row.key, From: from });
    }
}
